import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useEffect, useMemo} from 'react';
import {ROUTES} from '@/config/routesConstants';
import useIsMobile from '@/hooks/use-is-mobile';
import useEmberIframeStyleStore from '@/stores/emberIframeStyleStore';

function useSetEmberIframeStyles(
  emberIframeRef: React.RefObject<HTMLIFrameElement>,
  iframeLoaded: boolean,
  location: {pathname: string},
  additionalHeaderHeight = 0
) {
  const flags = useFlags();
  const isMobile = useIsMobile();
  const isCampaignPage = useMemo(() => {
    return RegExp(ROUTES.CAMPAIGN, 'i').test(location.pathname);
  }, [location.pathname]);

  const emberIframeStyleState = useEmberIframeStyleStore();
  const {headerHeight, menuHeight, sidebarWidth} = emberIframeStyleState;

  const headerHeightIncludingAdditional = headerHeight + additionalHeaderHeight;

  useEffect(() => {
    const emberDocument = emberIframeRef.current?.contentDocument;
    const emberApplicationElement =
      emberIframeRef.current?.contentDocument?.getElementsByClassName(
        'ember-application'
      )?.[0] as HTMLBodyElement;
    const protectedElement =
      emberIframeRef.current?.contentDocument?.getElementsByClassName(
        'protected'
      )?.[0] as HTMLDivElement;

    document?.documentElement?.style?.setProperty(
      '--brand-header-height',
      `${headerHeightIncludingAdditional}px`
    );
    document?.documentElement?.style?.setProperty(
      '--brand-menu-height',
      `${menuHeight}px`
    );
    document?.documentElement?.style?.setProperty(
      '--brand-sidenav-width',
      `${sidebarWidth}px`
    );

    if (emberDocument) {
      emberDocument?.documentElement?.style?.setProperty(
        '--iframe-padding-top',
        `${headerHeightIncludingAdditional}px`
      );

      emberDocument?.documentElement?.style?.setProperty(
        '--iframe-padding-left',
        `${sidebarWidth}px`
      );

      if (flags.reactCampaigns) {
        // Only add top padding for campaign pages when the flag is enabled
        // this is to account for the extra header space taken by the CampaignTabs
        emberDocument?.documentElement?.style?.setProperty(
          '--campaign-iframe-padding-top',
          `${isCampaignPage ? 52 : 0}px`
        );
      }
    }
    const iframeOutsideElementsHeight = isMobile
      ? menuHeight + headerHeightIncludingAdditional
      : headerHeightIncludingAdditional;

    if (protectedElement) {
      protectedElement.style.paddingTop = `${headerHeightIncludingAdditional}px`;
      protectedElement.style.height = `calc(100% - ${iframeOutsideElementsHeight}px)`;
      emberApplicationElement.style.paddingTop = '0px';
    } else if (emberApplicationElement) {
      emberApplicationElement.style.paddingTop = `${headerHeightIncludingAdditional}px`;
    }
  }, [
    emberIframeStyleState,
    additionalHeaderHeight,
    iframeLoaded,
    location,
    isMobile,
    isCampaignPage,
  ]);
}

export default useSetEmberIframeStyles;
